import axiosInstance, { getNewCancelToken } from '@/services/_AxiosInstance';

/**
 * `/master/asset`. Semua method balikin response axios UTUH, jadi jalur bacanya
 * `res.data.data` buat list dan `res.data.status` buat hasil mutasi.
 *
 * Bentuk satu baris asset, dibaca dari pemanggilnya:
 * - `massId` id asset (src/views/master/asset/Index.vue:139)
 * - `massName` nama tampil (Index.vue:111)
 * - `massCompId` dan `massDataId` id company, dikirim barengan waktu simpan
 *   (src/components/asset/Form.vue:317)
 * - `massSateliteId` id device satelit, dipakai buat nyocokin data jalur satelit
 *   (src/store/asset.js:143) — bisa kosong buat asset non-satelit
 * - `massImage` path relatif, ditampilin dengan prefix `/api/` (Index.vue:94)
 * - `massDevice` relasi device, CUMA ada di sebagian panggilan. store/asset.js:318
 *   me-rename-nya jadi `devices` dan ngisi `[]` kalau gak ada.
 *
 * Halaman ngirim `max` eksplisit karena default page backend kecil: `max: 1000` di
 * src/util/scopedAssets.js:8 dan src/views/bunkering_log/Index.vue:169,
 * `max: ACTIVE_ASSET_LIMIT` di src/views/master/daily_report/Index.vue:912.
 *
 * Sukses dicek lewat `res.data.status.error === 0`, BUKAN lewat HTTP status: data
 * invalid dibalikin `status.errorCode` 401 dengan HTTP 200
 * (src/components/asset/Form.vue:325 dan :335).
 */
export default {
  /**
   * GET `/master/asset` — daftar asset.
   *
   * @param {Object} [config] config axios; filter dan pagination masuk di
   *   `config.params`. Konvensi filter barunya `params: { 'finds[massName]': 'x' }`.
   *   Kirim `max` eksplisit kalau butuh lebih dari satu halaman default.
   * @returns {Promise<import('axios').AxiosResponse<import('@/services/_AxiosInstance').MasterListBody>>}
   *   response axios utuh — jalur bacanya `res.data.data` buat baris,
   *   `res.data.pagination.totalData` buat total
   */
  getAssets(config) {
    return axiosInstance.get('/master/asset', {
      ...config,
      cancelToken: getNewCancelToken(),
    });
  },
  getAsset(config) {
    return axiosInstance.get('/master/asset/', {
      ...config,
      cancelToken: getNewCancelToken(),
    });
  },
  /**
   * GET `/master/asset?findField=&findValue=` — konvensi filter LAMA, dirangkai manual
   * ke URL. Hidup berdampingan sama konvensi `finds[kolom]` di endpoint yang sama;
   * dua-duanya jalan, ini bukan kekeliruan pembaca.
   *
   * Namanya kembar sama `UserSvc.getAssetByName` yang mukul endpoint sama, tapi yang
   * punya pemanggil cuma yang di sini.
   *
   * @param {{findField: string, findValue: string}} param `findField` nama kolom
   *   mentah backend, mis. 'massName'; nilainya gak di-encode
   * @returns {Promise<import('axios').AxiosResponse<import('@/services/_AxiosInstance').MasterListBody>>}
   *   response axios utuh — jalur bacanya `res.data.data`
   */
  getAssetByName(param) {
    return axiosInstance.get(`/master/asset?findField=${param.findField}&findValue=${param.findValue}`, {
      cancelToken: getNewCancelToken(),
    });
  },
  getAssetByType(param) {
    return axiosInstance.get(`/master/asset?findField=${param.findField}&findValue=${param.findValue}`, {
      cancelToken: getNewCancelToken(),
    });
  },
  addAsset(data) {
    return axiosInstance.post('/master/asset', data, {
      cancelToken: getNewCancelToken(),
    });
  },
  updateAsset(id, data) {
    return axiosInstance.put(`/master/asset/${id}`, data, {
      cancelToken: getNewCancelToken(),
    });
  },
  /**
   * Create kalau `id` kosong, update kalau ada: POST `/master/asset` buat create,
   * PUT `/master/asset/{id}` buat update.
   *
   * CACAT NYATA, jangan diberesin di sini: `id` punya nilai default padahal dia
   * parameter NON-TERAKHIR, jadi defaultnya gak pernah kepakai dan pemanggil tetep
   * wajib ngirim dua argumen. Satu-satunya pemanggil
   * (src/components/asset/Form.vue:324) emang ngirim dua-duanya. Benerin tanda
   * tangannya = perubahan perilaku, task sendiri.
   *
   * Sukses dicek `res.data.status.error === 0`. Id record baru ada di `res.data.id`
   * (Form.vue:326). `status.errorCode` 401 = data invalid, datangnya lewat HTTP 200.
   *
   * @param {number|string|undefined} id id asset, kosong berarti create
   * @param {Object} data body asset; pas update, `massDevice` dan `massImage` dibuang
   *   dulu sama pemanggil (Form.vue:320)
   * @returns {Promise<import('axios').AxiosResponse<import('@/services/_AxiosInstance').MasterMutationBody>>}
   *   response axios utuh — jalur bacanya `res.data.status.error` dan `res.data.id`
   */
  addOrUpdateAsset(id = undefined, data) {
    if (id) {
      return this.updateAsset(id, data);
    }

    return this.addAsset(data);
  },
  /**
   * POST `/master/asset/upload/{id}` — upload gambar asset.
   *
   * Dikirim sebagai multipart dengan nama field `image`. FormData-nya dirakit di sini,
   * jadi pemanggil ngasih File/Blob mentah, bukan FormData.
   *
   * @param {number|string} id id asset; pas create, ambil dari `res.data.id` panggilan
   *   addOrUpdateAsset sebelumnya
   * @param {File|Blob} image berkas gambarnya
   * @returns {Promise<import('axios').AxiosResponse<import('@/services/_AxiosInstance').MasterMutationBody>>}
   *   response axios utuh — jalur bacanya `res.data.status.error`
   */
  uploadAssetImages(id, image) {
    const formData = new FormData();
    formData.append('image', image);
    return axiosInstance.post(`/master/asset/upload/${id}`, formData, {
      cancelToken: getNewCancelToken(),
    });
  },

  deleteAsset(id) {
    return axiosInstance.delete(`/master/asset/${id}`, {
      cancelToken: getNewCancelToken(),
    });
  },
};
